import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import toast from 'react-hot-toast';
import { Wallet, CheckCircle, XCircle, Clock, Eye, Plus } from 'lucide-react';

export default function WalletRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  // Direct add form
  const [showDirectAdd, setShowDirectAdd] = useState(false);
  const [directUserId, setDirectUserId] = useState('');
  const [directAmount, setDirectAmount] = useState('');
  const [directNote, setDirectNote] = useState('');
  const [users, setUsers] = useState([]);
  const [directLoading, setDirectLoading] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/wallet-request/admin/all?status=${filter}`);
      setRequests(data.requests || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRequests(); }, [filter]);

  useEffect(() => {
    if (showDirectAdd) {
      axios.get('/api/admin/users?limit=100').then(r => setUsers(r.data.users || []));
    }
  }, [showDirectAdd]);

  const handleApprove = async (id) => {
    try {
      const { data } = await axios.put(`/api/wallet-request/admin/${id}/approve`);
      toast.success(data.message);
      fetchRequests();
      setSelected(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const handleReject = async (id) => {
    const note = (() => {
      if (typeof window === 'undefined' || typeof window.prompt !== 'function') return 'Rejected';
      try {
        return window.prompt('Rejection reason:') || 'Rejected';
      } catch {
        return 'Rejected';
      }
    })();
    try {
      await axios.put(`/api/wallet-request/admin/${id}/reject`, { note });
      toast.success('Request rejected');
      fetchRequests();
      setSelected(null);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    }
  };

  const handleDirectAdd = async () => {
    if (!directUserId) return toast.error('Please select a user.');
    if (!directAmount || parseFloat(directAmount) <= 0) return toast.error('Please enter a valid amount.');
    setDirectLoading(true);
    try {
      const { data } = await axios.post(
        `/api/wallet-request/admin/direct-add/${directUserId}`,
        { amount: parseFloat(directAmount), note: directNote }
      );
      toast.success(data.message);
      setShowDirectAdd(false);
      setDirectUserId('');
      setDirectAmount('');
      setDirectNote('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally {
      setDirectLoading(false);
    }
  };

  const statusBadge = {
    pending: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
    approved: 'bg-primary-500/20 text-primary-300 border-primary-500/30',
    rejected: 'bg-red-500/20 text-red-300 border-red-500/30'
  };

  return (
    <AdminLayout>
      <div className="animate-fade-in">
        <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-white">Wallet Recharge Requests</h1>
            <p className="text-gray-400 mt-1">Manage user wallet recharge requests.</p>
          </div>
          <button onClick={() => setShowDirectAdd(!showDirectAdd)}
            className="bg-primary-600 hover:bg-primary-500 text-white px-4 py-2.5 rounded-xl font-medium flex items-center gap-2 transition-colors">
            <Plus className="w-4 h-4" /> Direct Add
          </button>
        </div>

        {/* Direct Add Panel */}
        {showDirectAdd && (
          <div className="bg-dark-700 border border-primary-500/30 rounded-2xl p-5 mb-6 animate-slide-up">
            <h3 className="text-white font-semibold mb-4">Direct Wallet Add (without request)</h3>
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Select User</label>
                <select value={directUserId} onChange={e => setDirectUserId(e.target.value)}
                  className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary-500">
                  <option value="">-- Select User --</option>
                  {users.map(u => (
                    <option key={u._id} value={u._id}>
                      {u.name} ({u.email}) — ${(u.mainWalletBalance || 0).toFixed(0)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Amount ($)</label>
                <input type="number" value={directAmount} onChange={e => setDirectAmount(e.target.value)}
                  className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary-500"
                  placeholder="Enter amount" min="1" />
              </div>
              <div>
                <label className="block text-xs text-gray-400 mb-1.5">Note (Optional)</label>
                <input type="text" value={directNote} onChange={e => setDirectNote(e.target.value)}
                  className="w-full bg-dark-600 border border-white/10 text-white rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-primary-500"
                  placeholder="Admin note..." />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={handleDirectAdd} disabled={directLoading}
                className="bg-primary-600 hover:bg-primary-500 disabled:bg-primary-800 text-white px-6 py-2.5 rounded-xl font-medium flex items-center gap-2 transition-colors">
                {directLoading
                  ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  : <><Wallet className="w-4 h-4" /> Add to Wallet</>}
              </button>
              <button onClick={() => setShowDirectAdd(false)}
                className="px-4 py-2.5 bg-dark-600 border border-white/10 text-gray-400 hover:text-white rounded-xl transition-colors">
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {['pending', 'approved', 'rejected'].map(s => (
            <button key={s} onClick={() => setFilter(s)}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors capitalize flex items-center gap-1.5
                ${filter === s ? 'bg-primary-600 text-white' : 'bg-dark-700 text-gray-400 border border-white/5 hover:text-white'}`}>
              {s === 'pending' && <Clock className="w-3.5 h-3.5" />}
              {s === 'approved' && <CheckCircle className="w-3.5 h-3.5" />}
              {s === 'rejected' && <XCircle className="w-3.5 h-3.5" />}
              {s}
            </button>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Requests List */}
          <div className="bg-dark-700 border border-white/5 rounded-2xl overflow-hidden">
            <div className="divide-y divide-white/5">
              {loading ? (
                <div className="py-12 text-center">
                  <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
                </div>
              ) : requests.length === 0 ? (
                <div className="py-12 text-center">
                  <Wallet className="w-10 h-10 text-gray-600 mx-auto mb-3" />
                  <p className="text-gray-500">No {filter} requests</p>
                </div>
              ) : requests.map(r => (
                <div key={r._id}
                  onClick={() => setSelected(r)}
                  className={`px-4 py-4 cursor-pointer hover:bg-white/2 transition-colors ${selected?._id === r._id ? 'bg-primary-500/10 border-l-2 border-l-primary-500' : ''}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-semibold">${r.amount.toFixed(2)}</p>
                      <p className="text-gray-400 text-sm">{r.user?.name}</p>
                      <p className="text-gray-600 text-xs">{r.user?.email}</p>
                      <p className="text-gray-600 text-xs mt-0.5">
                        {new Date(r.createdAt).toLocaleString('en-IN', {
                          day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
                        })}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className={`px-2 py-0.5 rounded-lg text-xs font-medium border ${statusBadge[r.status]}`}>
                        {r.status}
                      </span>
                      <p className="text-gray-500 text-xs mt-1">
                        Wallet: ${(r.user?.mainWalletBalance || 0).toFixed(0)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Detail Panel */}
          <div className="bg-dark-700 border border-white/5 rounded-2xl p-5">
            {!selected ? (
              <div className="flex items-center justify-center h-48 text-gray-600">
                <div className="text-center">
                  <Eye className="w-8 h-8 mx-auto mb-2" />
                  <p className="text-sm">Select a request to view details</p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-primary-500/20 flex items-center justify-center text-primary-400 font-bold">
                    {selected.user?.name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <p className="text-white font-semibold">{selected.user?.name}</p>
                    <p className="text-gray-400 text-sm">{selected.user?.email}</p>
                    <p className="text-gray-500 text-xs">
                      Current Balance: ${(selected.user?.mainWalletBalance || 0).toFixed(2)}
                    </p>
                  </div>
                </div>

                <div className="bg-dark-600 rounded-xl p-4 mb-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400">Amount Requested</span>
                    <span className="text-white font-bold text-lg">${selected.amount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">Status</span>
                    <span className={`px-2 py-0.5 rounded-lg text-xs font-medium border capitalize ${statusBadge[selected.status]}`}>
                      {selected.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-xs text-gray-400">
                    <div>
                      <p className="font-semibold text-white text-sm">Method</p>
                      <p className="mt-1">{selected.paymentMethod || 'UPI'}</p>
                    </div>
                    {selected.transactionId && (
                      <div>
                        <p className="font-semibold text-white text-sm">Txn ID</p>
                        <p className="mt-1 break-all">{selected.transactionId}</p>
                      </div>
                    )}
                  </div>
                  {selected.adminNote && (
                    <div className="mt-2 pt-2 border-t border-white/10">
                      <p className="text-gray-500 text-xs">Note: {selected.adminNote}</p>
                    </div>
                  )}
                </div>

                {/* Screenshot */}
                {selected.screenshot ? (
                  <div className="mb-4">
                    <p className="text-gray-400 text-xs mb-2">Payment Screenshot:</p>
                    <img src={selected.screenshot} alt="Payment proof"
                      className="w-full max-h-60 object-contain rounded-xl border border-white/10 bg-dark-600 p-2 cursor-pointer"
                      onClick={() => window.open(selected.screenshot, '_blank')} />
                    <p className="text-gray-600 text-xs mt-1 text-center">Click to open full size</p>
                  </div>
                ) : (
                  <div className="bg-dark-600 rounded-xl p-6 text-center mb-4">
                    <p className="text-gray-500 text-sm">No screenshot uploaded</p>
                  </div>
                )}

                {/* Actions */}
                {selected.status === 'pending' && (
                  <div className="flex gap-3">
                    <button onClick={() => handleApprove(selected._id)}
                      className="flex-1 bg-primary-600 hover:bg-primary-500 text-white font-semibold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors">
                      <CheckCircle className="w-4 h-4" /> Approve
                    </button>
                    <button onClick={() => handleReject(selected._id)}
                      className="flex-1 bg-red-600/20 border border-red-500/30 text-red-400 hover:bg-red-600/30 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 transition-colors">
                      <XCircle className="w-4 h-4" /> Reject
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
